/**
 * Menu — des actions au clic droit, ou déroulées sous un bouton (lot 3 du catalogue, 28-10-2026).
 *
 * Le modèle est le menu de Helios (apps/helios/src/menu.tsx — Marko, 05-10 : « que partout on
 * puisse faire clic droit et faire des actions ») ; le cockpit en avait vingt-huit écrits à la
 * main (`role="menu"`, `border-line bg-panel shadow-xl`). Il se dessine comme la liste du Select
 * (`.ch-menu`, `.ch-choix` de champs.css) : un menu et une liste déroulante sont la même chose
 * qui s'ouvre. Icône à gauche, un sous-texte gris, un raccourci à droite, la coche de la marque
 * sur le choix actif, le rouge de sens sur ce qui est `danger`.
 *
 *   const { ouvrir, ouvrirSous, menu } = useMenu();
 *   <tr onContextMenu={(e) => ouvrir(e, entrees)} />           ← au curseur
 *   <Button onClick={(e) => ouvrirSous(e.currentTarget, entrees)} />   ← sous le bouton, à sa largeur
 *   {menu}                                                      ← posé une fois dans le rendu
 *
 * Porté sur <body> (hors de la page qui l'ouvre : un panneau qui se ferme « au clic dehors »
 * prenait le clic sur un choix pour un clic dehors), dans le thème de l'ancre, jamais hors de
 * l'écran, en pixels de mise en page (le zoom du body, cf portail.ts). Un clic ailleurs, un
 * défilement, un redimensionnement, Échap ferment ; ↑ ↓ Début Fin parcourent, Entrée choisit.
 * Le clic ailleurs n'est armé qu'après 300 ms : sur l'appli de bureau (Electron, trackpad), le
 * clic droit émet un `pointerdown` parasite juste après, qui refermait le menu aussitôt.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cadreDuTheme, rectVue, vueDuCorps, type CadreTheme } from "./portail";
import { cn } from "./utils";
import "./champs.css";
import "./ouverture.css";

export interface ActionMenu {
  label: string;
  /** Après le label, gris : « 1 204 comptes ». */
  sous?: string;
  icone?: ReactNode;
  /** À droite, en chasse fixe : « ⌘K », « Suppr ». */
  raccourci?: string;
  /** Irréversible : en rouge. */
  danger?: boolean;
  /** Un choix parmi d'autres : `true` coche, `false` garde la place de la coche. */
  coche?: boolean;
  desactive?: boolean;
  onClick: () => void;
}

/** Une action, un trait (`"sep"`), ou un titre de section. */
export type EntreeMenu = ActionMenu | "sep" | { titre: string };

export interface MenuProps {
  /** Le point d'ancrage, en pixels de mise en page (le coin haut gauche ; haut droit si `alignement="droite"`). */
  x: number;
  y: number;
  entrees: EntreeMenu[];
  onClose: () => void;
  /** Le menu s'aligne sur le bord DROIT de son ancre (un bouton en bout de barre). */
  alignement?: "gauche" | "droite";
  /** La largeur minimale (celle du bouton, en menu déroulant). */
  largeur?: number;
  /** Le thème de l'endroit, recopié sur <body>. */
  cadre?: CadreTheme;
  ariaLabel?: string;
  /** Posé dans la page — le catalogue. */
  statique?: boolean;
  className?: string;
}

export function Menu({ x, y, entrees, onClose, alignement = "gauche", largeur, cadre, ariaLabel, statique = false, className }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const fermer = useRef(onClose);
  fermer.current = onClose;

  // Jamais hors de l'écran : recalé avant le premier affichage.
  useLayoutEffect(() => {
    if (statique) return;
    const { zoom, largeur: W, hauteur: H } = vueDuCorps();
    const r = rectVue(ref.current, zoom);
    if (!r) return;
    const marge = 8;
    const left = alignement === "droite" ? x - r.width : x;
    setPos({
      left: Math.max(marge, Math.min(left, W - r.width - marge)),
      top: Math.max(marge, Math.min(y, H - r.height - marge)),
    });
    ref.current?.focus({ preventScroll: true });
  }, [x, y, alignement, statique]);

  useEffect(() => {
    if (statique) return;
    let arme = false;
    const minuterie = setTimeout(() => {
      arme = true;
    }, 300);
    const siArme = () => {
      if (arme) fermer.current();
    };
    // Sa propre liste défile (au-delà de 320 px) : ce n'est pas « ailleurs », le menu reste ouvert.
    const auDefilement = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      siArme();
    };
    const auClavier = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        fermer.current();
      }
    };
    window.addEventListener("pointerdown", siArme);
    window.addEventListener("resize", siArme);
    window.addEventListener("blur", siArme);
    window.addEventListener("scroll", auDefilement, true);
    window.addEventListener("keydown", auClavier, true);
    return () => {
      clearTimeout(minuterie);
      window.removeEventListener("pointerdown", siArme);
      window.removeEventListener("resize", siArme);
      window.removeEventListener("blur", siArme);
      window.removeEventListener("scroll", auDefilement, true);
      window.removeEventListener("keydown", auClavier, true);
    };
  }, [statique]);

  function auClavier(e: KeyboardEvent<HTMLDivElement>) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const boutons = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>(".mn-item:not(:disabled)") ?? []);
    if (boutons.length === 0) return;
    const i = boutons.indexOf(document.activeElement as HTMLButtonElement);
    const n =
      e.key === "Home" ? 0
      : e.key === "End" ? boutons.length - 1
      : e.key === "ArrowDown" ? (i + 1) % boutons.length
      : i <= 0 ? boutons.length - 1
      : i - 1;
    boutons[n].focus();
  }

  const stop = (e: MouseEvent | PointerEvent) => e.stopPropagation();
  const liste = (
    <div
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      tabIndex={-1}
      className={cn("ch-menu ch-arrondi-menu mn", statique && "ch-menu-statique", className)}
      style={statique ? { minWidth: largeur } : { left: pos?.left ?? 0, top: pos?.top ?? 0, minWidth: largeur, visibility: pos ? undefined : "hidden" }}
      onKeyDown={auClavier}
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
      onContextMenu={(e) => e.preventDefault()}
    >
      {entrees.map((it, i) =>
        it === "sep" ? (
          <div key={i} className="ch-sep" role="separator" />
        ) : "titre" in it ? (
          <div key={i} className="ch-groupe ch-etiquette" role="presentation">
            {it.titre}
          </div>
        ) : (
          <button
            key={i}
            type="button"
            role={it.coche === undefined ? "menuitem" : "menuitemcheckbox"}
            aria-checked={it.coche === undefined ? undefined : it.coche}
            className="mn-item ch-choix"
            data-danger={it.danger || undefined}
            disabled={it.desactive}
            onClick={() => {
              fermer.current();
              it.onClick();
            }}
          >
            {it.icone !== undefined && <span className="mn-icone">{it.icone}</span>}
            <span className="mn-lib">
              {it.label}
              {it.sous && <span className="mn-sous">{it.sous}</span>}
            </span>
            {it.raccourci && <span className="mn-raccourci">{it.raccourci}</span>}
            {it.coche !== undefined && (
              <span className="mn-coche" data-on={it.coche || undefined} aria-hidden>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="m3.5 8.5 3 3 6-7" />
                </svg>
              </span>
            )}
          </button>
        ),
      )}
    </div>
  );

  if (statique) return liste;
  return createPortal(
    <div className={cadre?.sombre ? "dark" : undefined} data-theme={cadre?.theme}>
      {liste}
    </div>,
    document.body,
  );
}

interface EtatMenu {
  x: number;
  y: number;
  entrees: EntreeMenu[];
  alignement?: "gauche" | "droite";
  largeur?: number;
  cadre: CadreTheme;
}

/**
 * `ouvrir(e, entrees)` au curseur (`onContextMenu`), `ouvrirSous(bouton, entrees)` sous un
 * bouton à sa largeur (`alignement: "droite"` pour un bouton en bout de barre) ; `menu` se pose
 * une fois dans le rendu. Re-cliquer le bouton d'un menu ouvert le referme.
 */
export function useMenu(): {
  ouvrir: (e: MouseEvent, entrees: EntreeMenu[]) => void;
  ouvrirSous: (el: HTMLElement, entrees: EntreeMenu[], options?: { alignement?: "gauche" | "droite" }) => void;
  fermer: () => void;
  ouvert: boolean;
  menu: ReactNode;
} {
  const [etat, setEtat] = useState<EtatMenu | null>(null);
  const ancre = useRef<{ el: HTMLElement | null; ferme: number }>({ el: null, ferme: 0 });

  const ouvrir = useCallback((e: MouseEvent, entrees: EntreeMenu[]) => {
    e.preventDefault();
    e.stopPropagation();
    ancre.current.el = null;
    if (entrees.length === 0) return;
    const { zoom } = vueDuCorps();
    setEtat({ x: e.clientX / zoom, y: e.clientY / zoom, entrees, cadre: cadreDuTheme(e.currentTarget as Element) });
  }, []);

  const ouvrirSous = useCallback((el: HTMLElement, entrees: EntreeMenu[], options?: { alignement?: "gauche" | "droite" }) => {
    // re-cliquer le bouton d'un menu ouvert le referme (le pointerdown l'a déjà fermé juste avant ce clic)
    if (ancre.current.el === el && Date.now() - ancre.current.ferme < 300) return;
    ancre.current.el = el;
    if (entrees.length === 0) return;
    const { zoom } = vueDuCorps();
    const r = rectVue(el, zoom);
    if (!r) return;
    const alignement = options?.alignement ?? "gauche";
    setEtat({ x: alignement === "droite" ? r.right : r.left, y: r.bottom + 4, entrees, alignement, largeur: r.width, cadre: cadreDuTheme(el) });
  }, []);

  const fermer = useCallback(() => {
    ancre.current.ferme = Date.now();
    setEtat(null);
  }, []);

  const menu = etat ? <Menu {...etat} onClose={fermer} /> : null;
  return { ouvrir, ouvrirSous, fermer, ouvert: etat !== null, menu };
}
